export const LEXODY_PRO_ENTITLEMENT = 'lexody_pro';
export const DEFAULT_OFFERING = 'default';

let webConfig = null;

/**
 * Configure RevenueCat Web SDK (call once per logged-in user).
 * @see https://www.revenuecat.com/docs/getting-started/installation/web-sdk
 */
export function configureRevenueCatWeb(apiKey, appUserId) {
  webConfig = {
    webApiKey: apiKey || null,
    appUserId: appUserId ? String(appUserId) : null,
    purchaseLinks: {},
    purchasePackageIds: {},
  };
  return webConfig;
}

export function isConfigured() {
  return webConfig != null;
}

export function setPurchaseLinks(links) {
  if (!webConfig) webConfig = {};
  webConfig.purchaseLinks = links || {};
}

export function setPurchasePackageIds(packageIds) {
  if (!webConfig) webConfig = {};
  webConfig.purchasePackageIds = packageIds || {};
}

export async function getCustomerInfo() {
  // Web custom UI mode relies on backend sync; no direct SDK customer info on web.
  return null;
}

export async function getCurrentOffering() {
  return null;
}

/** True if Lexody Pro entitlement is active. */
export function hasLexodyPro(customerInfo) {
  const active = customerInfo && customerInfo.entitlements && customerInfo.entitlements.active;
  if (!active) return false;
  return (
    active[LEXODY_PRO_ENTITLEMENT] != null ||
    active['Lexody Pro'] != null ||
    active.premium != null
  );
}

export async function presentPaywall(options = {}) {
  throw new Error('Hosted paywall is disabled in custom UI mode.');
}

/**
 * RevenueCat Web Purchase Links require the App User ID as a path segment:
 * https://pay.rev.cat/<token>/<appUserId>
 * Using ?app_user_id= returns 404. See:
 * https://community.revenuecat.com/general-questions-7/issue-with-app-user-id-query-parameter-causing-404-error-in-web-purchase-link-6906
 */
export function buildPurchaseLinkUrl(baseUrl, appUserId, packageId) {
  const parsed = new URL(baseUrl.trim());
  parsed.searchParams.delete('app_user_id');
  if (packageId) {
    parsed.searchParams.set('package_id', packageId);
  }

  if (!appUserId) {
    return parsed.toString();
  }

  const userId = encodeURIComponent(String(appUserId));
  let pathname = parsed.pathname.replace(/\/+$/, '');
  const segments = pathname.split('/').filter(Boolean);
  const decodedLast =
    segments.length > 0 ? decodeURIComponent(segments[segments.length - 1]) : null;
  if (decodedLast !== String(appUserId)) {
    pathname = pathname + '/' + userId;
  }
  parsed.pathname = pathname;
  return parsed.toString();
}

export async function purchaseByProductId(productId) {
  if (!webConfig) {
    throw new Error('RevenueCat is not configured');
  }
  const links = webConfig.purchaseLinks || {};
  const packageIds = webConfig.purchasePackageIds || {};
  const baseUrl = links[productId];
  if (!baseUrl) {
    throw new Error(
      'No web purchase link configured for ' + productId + '. Set REVENUECAT_WEB_LINK_* env vars.'
    );
  }
  const packageId = packageIds[productId];
  const url = buildPurchaseLinkUrl(baseUrl, webConfig.appUserId, packageId);
  window.location.assign(url);
  return { redirected: true };
}

export async function restorePurchases() {
  const csrfMeta = document.querySelector('meta[name="csrf-token"]');
  const csrfToken = csrfMeta ? csrfMeta.content : null;
  const res = await fetch('/revenuecat_restore', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'X-CSRF-Token': csrfToken,
    },
  });
  const json = await res.json();
  if (!res.ok || json.error) {
    throw new Error(json.error || 'Restore failed');
  }
  return json.data || { synced: true };
}
